
import { useState } from 'react';
import { motion } from 'framer-motion';

export function FloorPlan() {
  const [activeType, setActiveType] = useState(0);

  const floorTypes = [
    {
      name: '타입 A',
      image: 'https://static.readdy.ai/image/9acf540dec95fde23784f4db934ca461/a204767beb1b9b63b6ba28b466c87520.png'
    },
    {
      name: '타입 B',
      image: 'https://static.readdy.ai/image/9acf540dec95fde23784f4db934ca461/ab2aee3309c9b3efdee25f6163ee41fb.png'
    }
  ];

  const features = [
    '지상 주차공간 없는 쾌적한 단지',
    '4Bay 구조로 채광과 통풍 우수',
    '전세대 남향 위주 설계',
    '판상형 구조로 프라이버시 확보'
  ];

  return (
    <section id="floorplan" className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            전용면적 84㎡(34평) 단일 타입
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600">
            556세대 6개 단지
          </p>
        </motion.div>

        {/* Floor Plan Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gray-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-xl mb-6 sm:mb-8"
        >
          {/* Type Selector - Mobile */}
          <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6 lg:hidden">
            {floorTypes.map((type, index) => (
              <button
                key={index}
                onClick={() => setActiveType(index)}
                className={`flex-1 px-4 py-2.5 sm:px-6 sm:py-3 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeType === index
                    ? 'bg-amber-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-amber-50 border border-gray-200'
                }`}
              >
                {type.name}
              </button>
            ))}
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            {/* Floor Plan Image */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg mb-6">
              <img
                src={floorTypes[activeType].image}
                alt={`84㎡ ${floorTypes[activeType].name}`}
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Floor Plan Info */}
            <div className="space-y-6">
              <div className="text-center sm:text-left">
                <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">전용면적</p>
                <p className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">84㎡(34평)</p>
                <p className="text-sm sm:text-base text-gray-600">공급면적 A타입:112.79㎡ B타입:113.21㎡</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex items-center gap-3 bg-white rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className="ri-layout-grid-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">구조</p>
                    <p className="font-semibold text-gray-900 text-sm">4Bay 판상형</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-white rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className="ri-sun-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">향</p>
                    <p className="font-semibold text-gray-900 text-sm">전세대 남향위주</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-900 mb-3">주요 특징</p>
                <ul className="space-y-2">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <i className="ri-check-line text-lg text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid grid-cols-5 gap-12 items-center">
            {/* Floor Plan Image */}
            <div className="col-span-3">
              {/* Type Selector */}
              <div className="flex gap-3 mb-6">
                {floorTypes.map((type, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveType(index)}
                    className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeType === index
                        ? 'bg-amber-600 text-white shadow-lg'
                        : 'bg-white text-gray-600 hover:bg-amber-50 border border-gray-200'
                    }`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <img
                  src={floorTypes[activeType].image}
                  alt={`84㎡ ${floorTypes[activeType].name}`}
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* Floor Plan Info */}
            <div className="col-span-2">
              <div className="space-y-8">
                <div>
                  <p className="text-sm text-gray-500 mb-2">전용면적</p>
                  <p className="text-5xl font-bold text-gray-900 mb-1">84㎡(34평)</p>
                  <p className="text-lg text-gray-600">공급면적 A타입:112.79㎡ B타입:113.21㎡</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg">
                      <i className="ri-layout-grid-line text-xl text-amber-600"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">구조</p>
                      <p className="font-semibold text-gray-900">4Bay 판상형(A타입 펜트리특화)(B타입 안방공간특화)</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg">
                      <i className="ri-sun-line text-xl text-amber-600"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">향</p>
                      <p className="font-semibold text-gray-900">전세대 남향위주</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-3">주요 특징</p>
                  <ul className="space-y-2">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <i className="ri-check-line text-lg text-amber-600 mt-0.5"></i>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Price Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-amber-600 to-amber-500 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 text-center shadow-xl"
        >
          <p className="text-white text-sm sm:text-base md:text-lg mb-1 sm:mb-2">분양가상한제 적용</p>
          <p className="text-white text-2xl sm:text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            4억원 대 합리적 분양가
          </p>
        </motion.div>
      </div>
    </section>
  );
}
